import { Application, Container, DisplayObject, InteractionEvent, Point, Ticker } from "pixi.js"
import { Direction, Joystick, JoystickSettings } from "pixi-virtual-joystick"

// How long a released stick takes to fade off the screen: long enough to read as the control
// letting go rather than a dropped frame, short enough that the next touch is not aiming at a
// ghost of the last one.
const FADE_MS = 140

// The eight compass sectors a stick reports, in order, from screen-right and going clockwise -- the
// direction of increasing atan2 once y points down the screen.
//
// Worked out here rather than taken from the library, whose own answer is wrong twice. Its sector
// table has a gap at exactly half a turn, so a dead-horizontal push to the LEFT matches no branch
// and falls out of the chain as top_right; and the axis-aligned shortcut that used to catch that
// case first reported `left` for a push to the right and `right` for a push to the left. A thumb
// almost never lands dead on an axis, which is how both survived.
const SECTORS: Direction[] = [
    Direction.RIGHT,
    Direction.BOTTOM_RIGHT,
    Direction.BOTTOM,
    Direction.BOTTOM_LEFT,
    Direction.LEFT,
    Direction.TOP_LEFT,
    Direction.TOP,
    Direction.TOP_RIGHT,
]

// A gesture that never pushed the stick this far out of its centre never steered anything, so it
// is a tap: the attack stick fires on one -- which is what pixi's own `tap` did for the pinned
// stick -- and the move stick does nothing with it. Expressed as a fraction of the travel, and the
// same fraction the handlers read as "no input" (GuiManager's JoystickDeadZone), so a tap is
// exactly a press that came back up without ever asking for anything.
const TAP_POWER = 0.3

/**
 * One touch stick, summoned under a finger instead of pinned to a corner.
 *
 * The library binds its own pointerdown/pointermove/pointerup ON ITSELF, which a stick that has to
 * appear mid-gesture can never receive: the press that summons it landed on empty ground, and by
 * the time the stick is under the finger pixi has already dispatched that press elsewhere. So all
 * of that binding is dropped (see bindEvents) and `FloatingSticks` drives grab/steer/release from
 * the interaction manager's own event stream instead.
 *
 * What is kept from the library is the part worth keeping: the two-sprite layout, the radii it
 * derives from them, and its power/direction maths -- the eight compass sectors the game moves in.
 */
export class FloatingJoystick extends Joystick {
    // pixi's id for the finger holding this stick, or null when nothing holds it. A touch's id and
    // a mouse's pointer id are different numbers for the same gesture, but every id here comes out
    // of the one normalized stream FloatingSticks listens to, so they always compare.
    private holder: number | null = null
    // The furthest the finger has been from where it went down, in this stick's own pixels. What
    // separates a tap from a steer.
    private reach = 0
    private fadeLeft = 0

    constructor(opts: JoystickSettings) {
        super(opts)
        this.visible = false
        this.alpha = 0
        // A stick is PAINTED, never pressed. Staying out of the display tree's hit testing is what
        // lets it be drawn over the world without shadowing the entity underneath it, and what
        // keeps the hit test answering "free space" for ground a stick happens to cover.
        this.interactive = false
        this.interactiveChildren = false
    }

    // Deliberately empty: see the class comment.
    protected bindEvents(): void {}

    isHeld = (): boolean => this.holder !== null

    heldBy = (pointerId: number): boolean => this.holder === pointerId

    /**
     * Put the stick under a finger and take hold of it. `at` is in the parent's coordinates, and
     * becomes the stick's centre: deflection is measured from where the finger landed, so the knob
     * starts dead centre however far from a corner the player touched.
     */
    grab = (pointerId: number, at: Point) => {
        this.stopFade()
        this.holder = pointerId
        this.reach = 0
        this.position.set(at.x, at.y)
        this.inner.position.set(0, 0)
        this.inner.alpha = 1
        this.alpha = 1
        this.visible = true
        this.settings.onStart?.()
    }

    /** Steer: `at` is where the finger is now, in the parent's coordinates. */
    steer = (at: Point) => {
        if (this.holder === null) {
            return
        }
        const dx = at.x - this.position.x
        const dy = at.y - this.position.y
        const distance = Math.sqrt(dx * dx + dy * dy)
        this.reach = Math.max(this.reach, distance)

        // The knob tracks the finger out to the rim and stops there, keeping the direction. The
        // library clamped each axis separately, which bent a diagonal into the corner of a square
        // once either axis ran out; clamping the vector's length holds the angle the thumb chose.
        const pulled = Math.min(distance, this.outerRadius)
        const knob = distance > 0 ? new Point((dx / distance) * pulled, (dy / distance) * pulled) : new Point(0, 0)
        this.inner.position.set(knob.x, knob.y)
        if (distance === 0) {
            return
        }

        const bearing = Math.atan2(dy, dx)
        this.settings.onChange?.({
            // Degrees counter-clockwise from screen-right, the convention the library's own diagram
            // documents. Nothing in the HUD reads it -- direction and power are the whole signal --
            // but it is part of the event the handlers are typed against.
            angle: ((-bearing * 180) / Math.PI + 360) % 360,
            direction: SECTORS[(Math.round(bearing / (Math.PI / 4)) + 8) % 8],
            // Exactly how far out of its travel the thumb is: a ratio of the two lengths rather
            // than a re-measurement of the clamped knob, so a thumb at or past the rim reads as a
            // clean 1 and the sprint and fire thresholds have something exact to compare against.
            power: pulled / this.outerRadius,
        })
    }

    /**
     * Let go, and start fading out. Answers whether the gesture was a tap -- a press that went
     * down and came up without ever steering.
     */
    release = (): { tapped: boolean } => {
        if (this.holder === null) {
            return { tapped: false }
        }
        this.holder = null
        this.inner.position.set(0, 0)
        this.inner.alpha = this.innerAlphaStandby
        this.settings.onEnd?.()
        this.fadeLeft = FADE_MS
        Ticker.shared.remove(this.fade)
        Ticker.shared.add(this.fade)
        return { tapped: this.reach <= this.outerRadius * TAP_POWER }
    }

    private fade = () => {
        this.fadeLeft -= Ticker.shared.deltaMS || 0
        if (this.fadeLeft <= 0) {
            this.stopFade()
            return
        }
        this.alpha = this.fadeLeft / FADE_MS
    }

    private stopFade = () => {
        this.fadeLeft = 0
        this.alpha = 0
        this.visible = false
        Ticker.shared.remove(this.fade)
    }
}

export interface FloatingSticksProps {
    app: Application
    /** The HUD layer the sticks are drawn in. Touch positions are converted into its coordinates. */
    layer: Container
    move: FloatingJoystick
    aim: FloatingJoystick
    /** The screen's horizontal middle, in the layer's coordinates. Read per touch, not cached. */
    midline: () => number
    /** A tap on the attack side, which fires without any steering. */
    onAimTap: () => void
    /**
     * Where a thumb summoning the attack stick landed, in pixi global coordinates. Called BEFORE the
     * stick takes hold, so a touch can aim at the spot it landed on, as a click does, before the
     * stick's own onStart starts the attack.
     */
    onAimDown?: (global: Point) => void
    /**
     * Whether a press that landed on something interactive is free space after all: an entity that
     * offers this viewer nothing has no use for the touch, which belongs to the world -- the tap on an
     * enemy that a click on one has always been on desktop.
     */
    pressFallsThrough?: (hit: DisplayObject) => boolean
    /** Optional mobile build touch owner. Return true to keep the touch away from sticks. */
    onWorldDown?: (event: InteractionEvent) => boolean
    onWorldMove?: (event: InteractionEvent) => void
    onWorldUp?: (cancelled?: boolean) => void
    buildMode?: () => boolean
    movementZone?: (at: Point) => boolean
}

/**
 * The gesture that summons the two touch sticks.
 *
 * The sticks used to be bolted to the bottom corners, which costs a thumb's worth of screen on a
 * phone whether or not anyone is touching it, and asks the player to find a fixed spot by feel. So
 * a stick now appears wherever a free touch lands: the left half of the screen summons the move
 * stick, the right half the attack stick, and each half ignores further touches while a thumb
 * already holds its stick.
 *
 * "Free" means the touch landed on nothing that wanted it. Presses are asked about rather than
 * intercepted -- pixi is given the point and answers what is under it -- so an entity with something
 * to offer, a widget, a dialog's close button or anything else interactive keeps its press and
 * summons nothing, and no control has to opt out. A hit on nothing, or on the stage (what pixi
 * answers for the ground, the tiles and any other art that is drawn but not interactive) is free
 * space, and so is an entity that offers nothing (see pressFallsThrough).
 *
 * Every event comes off the interaction manager rather than the display tree, because the stick is
 * not there to receive it when the gesture starts, and because these are the only handlers that see
 * a touch released far from where it began. The manager normalizes touch and pointer events into
 * one stream with a stable id per finger, which is how two thumbs stay on their own sticks.
 */
export class FloatingSticks {
    private readonly props: FloatingSticksProps
    private worldPointer: number | null = null

    constructor(props: FloatingSticksProps) {
        this.props = props
    }

    bind = (): boolean => {
        const interaction = this.interaction()
        if (!interaction) {
            return false
        }
        interaction.on("pointerdown", this.onDown)
        interaction.on("pointermove", this.onSteer)
        // A finger released over the canvas, released off it, and taken away by the system (a
        // notification, an incoming call) all have to let the stick go. Missing any one of them
        // leaves the intent latched and the player walking with nothing on the screen.
        interaction.on("pointerup", this.onUp)
        interaction.on("pointerupoutside", this.onUp)
        interaction.on("pointercancel", this.onCancel)
        window.addEventListener("blur", this.onBlur)
        return true
    }

    private onDown = (event: InteractionEvent) => {
        const { move, aim, layer, midline } = this.props
        const at = layer.toLocal(event.data.global)
        if (this.props.buildMode?.() && !this.props.movementZone?.(at)) {
            if (this.worldPointer === null && this.props.onWorldDown?.(event)) this.worldPointer = event.data.identifier
            return
        }
        if (!this.isFreeSpace(event)) {
            return
        }
        const stick = this.props.buildMode?.() || at.x < midline() ? move : aim
        if (stick.isHeld()) {
            return
        }
        if (stick === aim) this.props.onAimDown?.(event.data.global)
        stick.grab(event.data.identifier, at)
    }

    private onSteer = (event: InteractionEvent) => {
        if (this.worldPointer === event.data.identifier) {
            this.props.onWorldMove?.(event)
            return
        }
        const stick = this.holdingStick(event.data.identifier)
        stick?.steer(this.props.layer.toLocal(event.data.global))
    }

    private onUp = (event: InteractionEvent) => {
        if (this.worldPointer === event.data.identifier) {
            this.worldPointer = null
            this.props.onWorldUp?.()
            return
        }
        const stick = this.holdingStick(event.data.identifier)
        if (!stick) {
            return
        }
        const { tapped } = stick.release()
        if (tapped && stick === this.props.aim) {
            this.props.onAimTap()
        }
    }

    private onCancel = (event: InteractionEvent) => {
        if (this.worldPointer === event.data.identifier) {
            this.worldPointer = null
            this.props.onWorldUp?.(true)
        }
        this.holdingStick(event.data.identifier)?.release()
    }

    releaseAll = () => this.onBlur()

    private onBlur = () => {
        if (this.worldPointer !== null) this.props.onWorldUp?.(true)
        this.worldPointer = null
        this.props.move.release()
        this.props.aim.release()
    }

    private holdingStick = (pointerId: number): FloatingJoystick | undefined => {
        const { move, aim } = this.props
        return move.heldBy(pointerId) ? move : aim.heldBy(pointerId) ? aim : undefined
    }

    /**
     * Did this touch land on something that wants it?
     *
     * `undefined` from the hit test means the question cannot be answered here (no interaction
     * plugin, i.e. headless tests), which is read as "do not summon" rather than as "landed on
     * nothing" -- the same way Mouse.pressTarget's callers read it.
     */
    private isFreeSpace = (event: InteractionEvent): boolean => {
        const hit = this.hitTest(event.data.global)
        if (hit === undefined) return false
        return hit === null || hit === this.props.app.stage || !!this.props.pressFallsThrough?.(hit)
    }

    private hitTest = (at: Point): DisplayObject | null | undefined => {
        const interaction = this.interaction()
        if (!interaction?.hitTest) {
            return undefined
        }
        return interaction.hitTest(at) || null
    }

    private interaction = (): any => (this.props.app.renderer as any)?.plugins?.interaction
}
